import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download } from 'lucide-react';
import { formatDate, pad2 } from '../format';
import ScoreHero from './ScoreHero';
import CompetitorChart from './CompetitorChart';
import QuestionsList from './QuestionsList';
import SourcesTable from './SourcesTable';
import TechnicalChecks from './TechnicalChecks';
import ReasonsList from './ReasonsList';
import ActionPlan from './ActionPlan';
import DoneForYou from './DoneForYou';
import MonitoringInterest from './MonitoringInterest';
import LegalLinks from '../../../components/LegalLinks';
import '../../landing/landing.css';
import '../report.css';

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const Section = ({ index, title, intro, children }) => (
  <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
    <p className="lp-kicker">{pad2(index)}</p>
    <h2 className="lp-display mt-4 max-w-3xl text-[clamp(2.2rem,4.8vw,3.75rem)]">{title}</h2>
    {intro && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-[var(--lp-muted)]">{intro}</p>}
    <div className="mt-12">{children}</div>
  </section>
);

// Renders a full report that matches supabase/functions/_shared/ai-report/schema.json.
// `reportId` (real reports only) enables the monthly follow-up waitlist.
const ReportView = ({ report, reportId = null, isExample = false }) => {
  const rootRef = useRef(null);
  const { business, summary, methodology } = report;
  const date = formatDate(report.generatedAt);

  // Printing ignores closed <details>, so open them all first (covers both
  // the button and Ctrl+P).
  useEffect(() => {
    const openAll = () => rootRef.current?.querySelectorAll('details').forEach((d) => d.setAttribute('open', ''));
    window.addEventListener('beforeprint', openAll);
    return () => window.removeEventListener('beforeprint', openAll);
  }, []);

  return (
    <div ref={rootRef} className="lp rp">
      <header className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-6 sm:px-6 lg:px-8">
        <Link to="/" className="lp-display text-2xl">
          webspector<span className="text-[var(--lp-lime)]">.</span>
        </Link>
        <div className="no-print flex flex-wrap items-center gap-3 text-sm">
          {isExample && (
            <span className="rounded-full border border-[var(--lp-lime)] px-3 py-1.5 font-semibold">
              Informe de ejemplo · negocio ficticio
            </span>
          )}
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--lp-line)] px-4 py-2 font-semibold transition-colors hover:border-[var(--lp-text)]"
          >
            <Download size={16} aria-hidden="true" />
            Descargar PDF
          </button>
        </div>
      </header>

      <main>
        {/* Cover */}
        <section className="mx-auto max-w-6xl px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pb-24 lg:pt-16">
          <p className="lp-kicker">Informe GEO · Visibilidad en IA{isExample ? ' · Ejemplo' : ''}</p>
          <h1 className="lp-display mt-6 text-[clamp(3rem,8vw,6.5rem)] leading-[0.95]">{business.name}</h1>
          <p className="mt-5 text-lg text-[var(--lp-muted)]">
            {capitalize(business.sector)} · {business.city} · {date}
          </p>
          <p className="rp-headline lp-serif mt-10 max-w-4xl text-[clamp(1.75rem,3.6vw,2.75rem)] leading-[1.2]">
            {summary.headline}
          </p>
        </section>

        <section className="border-y border-[var(--lp-line)]">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
            <ScoreHero report={report} />
          </div>
        </section>

        <Section
          index={1}
          title="Quién se lleva las recomendaciones"
          intro="Cada barra es el porcentaje de respuestas de la IA en las que aparece cada negocio."
        >
          <CompetitorChart report={report} />
        </Section>

        <Section
          index={2}
          title="Lo que preguntan tus clientes"
          intro={
            methodology.samplesPerQuestion > 1
              ? `Hicimos ${summary.questionsAsked} preguntas como las haría un cliente, cada una ${methodology.samplesPerQuestion} veces, porque la IA no responde siempre igual.`
              : `Hicimos ${summary.questionsAsked} preguntas distintas, como las haría un cliente: así ves en cuáles te nombra la IA y a quién recomienda en las demás.`
          }
        >
          <QuestionsList report={report} />
        </Section>

        <Section
          index={3}
          title="Dónde busca la IA"
          intro="Las webs que la IA consultó para responder. Estar en las más citadas es de lo que más ayuda."
        >
          <SourcesTable report={report} />
        </Section>

        <Section index={4} title="Tu web, vista por la IA" intro="Lo que la IA encuentra cuando lee tu página.">
          <TechnicalChecks report={report} />
        </Section>

        <Section index={5} title="Por qué te ganan" intro="Cada motivo va con las pruebas en las que se basa.">
          <ReasonsList report={report} />
        </Section>

        <Section
          index={6}
          title="Tu plan de acción"
          intro="Ordenado por impacto: empieza por arriba. Marca los pasos a medida que los hagas."
        >
          <ActionPlan report={report} storageKey={`webspector-plan:${business.name}:${report.generatedAt}`} />
        </Section>

        <Section
          index={7}
          title="Hecho para ti"
          intro="Los textos para aplicar el plan, ya escritos. Cópialos, completa lo que está entre corchetes y pégalos."
        >
          <DoneForYou report={report} />
        </Section>

        {reportId && !isExample && <MonitoringInterest reportId={reportId} businessName={business.name} />}

        {/* Methodology */}
        <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8">
          <div className="rp-keep rounded-[24px] border border-[var(--lp-line)] p-6 sm:p-8">
            <p className="lp-kicker">Cómo se ha hecho este informe</p>
            <div className="mt-4 max-w-3xl space-y-3 leading-relaxed text-[var(--lp-muted)]">
              <p>
                El {date} hicimos {summary.questionsAsked} preguntas como las haría un cliente a una{' '}
                {methodology.engineLabel}
                {methodology.samplesPerQuestion > 1
                  ? `, cada una ${methodology.samplesPerQuestion} veces: ${summary.answersAnalyzed} respuestas en total.`
                  : ', una respuesta por pregunta.'}{' '}
                Los negocios, puestos y fuentes salen de esas respuestas tal cual.
              </p>
              <p>
                La puntuación suma, por cada respuesta en la que apareces, 1 punto si sales el primero, 0,85 si sales el
                segundo, 0,7 el tercero y 0,55 más abajo, y lo divide entre el total de respuestas. Un 100 sería salir
                el primero en todas.
              </p>
              <p>
                Cada asistente de IA (ChatGPT, Gemini, Perplexity…) responde a su manera y las respuestas cambian con
                el tiempo: tómalo como una señal fiable, no como una foto exacta de lo que verá cada persona.
              </p>
            </div>
          </div>
        </section>

        {isExample && (
          <section className="no-print bg-[var(--lp-lime)] text-[var(--lp-ink)]">
            <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-16 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-8 lg:py-20">
              <h2 className="lp-display text-[clamp(2.5rem,6vw,4.75rem)]">
                ¿Quieres el de
                <br />
                <span className="lp-serif">tu negocio?</span>
              </h2>
              <Link to="/#comprobar" className="lp-btn lp-btn--ink self-start lg:self-auto">
                Empieza con la comprobación gratis
                <ArrowRight size={20} strokeWidth={2.6} aria-hidden="true" />
              </Link>
            </div>
          </section>
        )}
      </main>

      <footer className="no-print mx-auto flex max-w-6xl flex-col gap-3 px-4 py-10 lp-mono text-[11px] uppercase tracking-[0.14em] text-[var(--lp-muted)] sm:flex-row sm:justify-between sm:px-6 lg:px-8">
        <span>© {new Date().getFullYear()} webspector · GEO para negocios locales</span>
        <LegalLinks />
      </footer>
    </div>
  );
};

export default ReportView;
